import React, { useContext, useEffect, useState } from 'react';
import proptypes from 'prop-types';
const { bool, string } = proptypes;

import {
  arrayOf as arrayOfType,
  bool as boolType,
  extend,
  number as numType,
  params,
  shape as shapeType,
  string as stringType,
  useDataModel
} from '@thd-nucleus/data-sources';
import { Skeleton, SkeletonContent, SkeletonBlock } from '@one-thd/sui-atomic-components';
import { ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';
import { Image } from '@thd-olt-component-react/core-ui';
import { Hotspot } from './subcomponents/Hotspot';
import { Toggle } from './subcomponents/Toggle';
import { getUniqueHotspots } from '../helpers';
import { MAX_DISPLAYED_HOTSPOTS_COUNT } from '../constants';
import './product-hotspots.scss';

const ProductHotspots = ({
  collectionId,
  preview,
  schemaName,
  skipCollections,
}) => {
  const { channel } = useContext(ExperienceContext);
  const storeId = useStoreId();

  const [shouldHideHotspots, setShouldHideHotspots] = useState(false);
  const [currentSelectedHotspotIndex, setCurrentSelectedHotspotIndex] = useState(null);

  const onToggle = (hidden) => {
    setShouldHideHotspots(!hidden);
  };

  useEffect(() => {
    /* istanbul ignore else */
    if (LIFE_CYCLE_EVENT_BUS) {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('product-hotspots.ready');
    }
  }, []);

  const { data, loading } = useDataModel('collection', {
    variables: {
      collectionId: collectionId || '',
      storeId,
      preview,
    },
    ssr: false,
    skip: skipCollections
  });

  const { collection } = data || {};
  const { imageBoundBox, metadata = {}, products } = collection || {};
  const { primaryGalleryImage = {} } = metadata;
  const { images = [] } = primaryGalleryImage;
  const [primaryImage] = images;

  const uniqueHotspots = getUniqueHotspots(imageBoundBox);

  if (loading || !primaryImage) {

    return (
      <Skeleton orientation="horizontal" disablePadding disableGutters>
        <SkeletonContent disablePadding disableGutters>
          <SkeletonBlock height={60} width={60} aspect="wide" />
          <SkeletonBlock height={60} width={60} aspect="wide" />
          <SkeletonBlock height={60} width={60} aspect="wide" />
        </SkeletonContent>
        <SkeletonContent disablePadding disableGutters>
          <SkeletonBlock height={60} width={60} aspect="wide" />
          <SkeletonBlock height={60} width={60} aspect="wide" />
          <SkeletonBlock height={60} width={60} aspect="wide" />
        </SkeletonContent>
        <SkeletonContent disablePadding disableGutters>
          <SkeletonBlock height={60} width={60} aspect="wide" />
          <SkeletonBlock height={60} width={60} aspect="wide" />
          <SkeletonBlock height={60} width={60} aspect="wide" />
        </SkeletonContent>
      </Skeleton>
    );
  }

  return (
    <div className="product-hotspots" data-component="ProductHotspots">
      <Image
        alt="background"
        className="mainImage stretchy"
        src={primaryImage?.url}
        height="1"
        width="1"
      />
      {uniqueHotspots && products && <Toggle onToggle={onToggle} />}
      {(uniqueHotspots && products && !shouldHideHotspots)
        && uniqueHotspots.slice(0, MAX_DISPLAYED_HOTSPOTS_COUNT).map((hotspot, index) => {
          const { hsXPosition, hsYPosition, itemId } = hotspot;
          const sku = products.find((product) => Number(product.itemId) === Number(itemId));

          if (!sku) return null;

          if (typeof LIFE_CYCLE_EVENT_BUS !== 'undefined') {
            LIFE_CYCLE_EVENT_BUS.trigger('product-hotspots.render', {
              channel,
              itemID: itemId,
              leftPosition: hsXPosition,
              sku,
              topPosition: hsYPosition
            });
          }

          return (
            <Hotspot
              hsXPosition={hsXPosition}
              hsYPosition={hsYPosition}
              itemID={itemId}
              key={itemId}
              product={sku}
              storeId={storeId}
              schemaName={schemaName}
              index={index}
              currentHotspotIndex={currentSelectedHotspotIndex}
              setCurrentHotspotIndex={setCurrentSelectedHotspotIndex}
            />
          );
        })}
    </div>
  );
};

ProductHotspots.displayName = 'ProductHotspots';

const productHotspot = arrayOfType({
  hsXPosition: numType({ float: true }),
  hsYPosition: numType({ float: true }),
  itemId: stringType()
});

if (productHotspot.skip) {
  productHotspot.skip('skipImageBoundBox', false);
}

const product = extend(
  {
    itemId: stringType()
  },
  Hotspot.dataModel.product
);

ProductHotspots.dataModel = extend({
  collection: params({
    collectionId: stringType().isRequired(),
    storeId: stringType(),
    preview: boolType()
  }).shape({
    imageBoundBox: productHotspot,
    metadata: shapeType({
      primaryGalleryImage: shapeType({
        images: arrayOfType({
          url: stringType()
        })
      })
    }),
    products: arrayOfType(shapeType(product))
  })
}, Hotspot);

ProductHotspots.propTypes = {
  collectionId: string,
  preview: bool,
  schemaName: string,
  skipCollections: bool
};

ProductHotspots.defaultProps = {
  collectionId: '',
  preview: false,
  schemaName: '',
  skipCollections: false
};

export { ProductHotspots };
